import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Input,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Grid2 from '@mui/material/Unstable_Grid2';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import ModalV2 from '/@/components/bayer/modal-v2';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { MuiSelect } from '/@/mui/select';
import { editProfile } from '/@/store/session/actions';
import { getSessionCountry, getUser } from '/@/store/session/selectors';
import AREA_CODES from '/@/utils/area-codes';
import { validatePhoneNumber } from '/@/utils/phone-numbers';
import { reverse } from '/@/utils/router';
import deleteAccount from '../../../user-form/useDeleteAccount';
import type { ProfileV2FormData } from '../..';
import FormSection from '../../components/form-section';
import TabContainer from '../../components/tab-container';

const DeleteAccountModal = ({ open, setOpen }: { open: boolean; setOpen: (open: boolean) => void }) => {
    const navigate = useNavigate();
    const user = useSelector(getUser);
    const [openConfirmDeleteSuccess, setOpenConfirmDeleteSuccess] = useState(false);
    const [errorPassword, setErrorPassword] = useState('');

    const form = useForm<{ password: string; confirmPassword: string }>({
        defaultValues: {
            password: '',
        },
    });

    const submitFormDeleteAccount = async (data: { password: string; confirmPassword: string }) => {
        const success = await deleteAccount({
            user,
            password: data.password,
            setErrorPassword: (error: string) => {
                setErrorPassword(error);
            },
        });
        if (success) {
            setOpenConfirmDeleteSuccess(true);
            setOpen(false);
        }
    };

    return (
        <>
            <ModalV2
                open={open}
                onClose={() => setOpen(false)}
                title={_('¿Querés eliminar tu cuenta de {{appName}}?', {
                    appName: settings.app.name,
                })}
                actions={
                    <>
                        <Button variant='text' color='secondary' onClick={() => setOpen(false)}>
                            {_('Cancelar')}
                        </Button>
                        <Button
                            variant='contained'
                            color='secondary'
                            onClick={() => submitFormDeleteAccount(form.getValues())}
                            disabled={!form.formState.isValid}
                        >
                            {_('Eliminar cuenta')}
                        </Button>
                    </>
                }
                actionsProps={{
                    justifyContent: 'end',
                }}
                maxWidth='md'
            >
                <Stack
                    spacing={2}
                    p={3}
                    sx={{
                        textAlign: 'start',
                    }}
                >
                    <Typography variant='body1' color='primary'>
                        {_(
                            'Si eliminás tu cuenta, vas a perder el acceso a toda tu información en {{appName}} (productores, lotes, recomendaciones y reportes). Esta acción es permanente y no se puede deshacer.',
                            {
                                appName: settings.app.name,
                            }
                        )}
                    </Typography>
                    <Input
                        type='password'
                        placeholder={_('Contraseña')}
                        {...form.register('password', { required: true })}
                        fullWidth
                        error={!!errorPassword}
                    />
                    {errorPassword && (
                        <Typography variant='body2' color='error' sx={{ mt: 1 }}>
                            {errorPassword}
                        </Typography>
                    )}
                </Stack>
            </ModalV2>

            <ModalV2
                open={openConfirmDeleteSuccess}
                onClose={() => navigate(reverse('NewHome'))}
                title={_('Cuenta eliminada con éxito')}
            >
                <Stack p={3}>
                    <Typography variant='body1' color='primary'>
                        {_('Tu cuenta de {{appName}} fue eliminada. Ya no tendrás acceso a la plataforma.', {
                            appName: settings.app.name,
                        })}
                    </Typography>
                </Stack>
            </ModalV2>
        </>
    );
};

export interface ProfileFormData {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    type: string;
    subtype: string;
    receiveNotifications: boolean;
    receiveWhatsappNotifications: boolean;
}

const ProfileEditTab = forwardRef(({ form }: { form: ReturnType<typeof useForm<ProfileV2FormData>> }, ref) => {
    const user = useSelector(getUser);
    const dispatch = useDispatch();
    const [openDeleteAccountPopup, setOpenDeleteAccountPopup] = useState(false);
    const country = useSelector(getSessionCountry);
    const theme = useTheme();

    useEffect(() => {
        form.reset({
            ...form.getValues(),
            firstName: user?.first_name,
            lastName: user?.last_name,
            email: user?.email,
            phone: user?.phone,
            type: user?.type,
            subtype: user?.subtype,
            receiveNotifications: user?.receive_notifications,
            receiveWhatsappNotifications: user?.receive_whatsapp_notifications,
        });
    }, [user, form]);

    const onSubmit = useCallback(
        async (data: ProfileFormData) => {
            await dispatch(
                editProfile({
                    firstName: data.firstName,
                    lastName: data.lastName,
                    email: data.email,
                    phone: data.phone,
                    type: data.type,
                    subtype: data.subtype,
                    receiveNotifications: data.receiveNotifications,
                    receiveWhatsappNotifications: data.receiveWhatsappNotifications,
                    //
                    showSlides: user?.show_slides,
                })
            );
        },
        [user, dispatch]
    );

    useImperativeHandle(
        ref,
        () => ({
            onSubmit,
        }),
        [onSubmit]
    );

    return (
        <TabContainer title={_('Información personal')}>
            <Grid2 container columns={9}>
                <Grid2 xs={12} lg={6}>
                    <Stack spacing={4}>
                        <Stack spacing={2}>
                            <FormSection title={_('Datos personales')}>
                                <Grid2 xs={12} md={6}>
                                    <TextField
                                        label={_('Nombre')}
                                        {...form.register('firstName', {
                                            required: _('El nombre es requerido'),
                                            minLength: {
                                                value: 2,
                                                message: _('El nombre debe tener al menos 2 caracteres'),
                                            },
                                        })}
                                        error={!!form.formState.errors.firstName}
                                        helperText={form.formState.errors.firstName?.message}
                                        fullWidth
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6}>
                                    <TextField
                                        label={_('Apellido')}
                                        {...form.register('lastName', {
                                            required: _('El apellido es requerido'),
                                            minLength: {
                                                value: 2,
                                                message: _('El apellido debe tener al menos 2 caracteres'),
                                            },
                                        })}
                                        error={!!form.formState.errors.lastName}
                                        helperText={form.formState.errors.lastName?.message}
                                        fullWidth
                                    />
                                </Grid2>
                            </FormSection>

                            <FormSection title={_('Información de contacto')}>
                                <Grid2 xs={12} md={6}>
                                    <TextField
                                        type='email'
                                        label={_('Email')}
                                        value={form.watch('email')}
                                        disabled
                                        fullWidth
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6}>
                                    <Controller
                                        control={form.control}
                                        name='phone'
                                        rules={{
                                            validate: {
                                                validPhone: (v) => {
                                                    if (!v || v === '') return true;
                                                    if (!validatePhoneNumber(v))
                                                        return _('El formato del número de teléfono es incorrecto.');
                                                    return true;
                                                },
                                            },
                                        }}
                                        render={({ field, fieldState }) => {
                                            return (
                                                <FormControl
                                                    fullWidth
                                                    sx={{
                                                        height: 'auto',
                                                    }}
                                                    ref={field.ref}
                                                >
                                                    <PhoneInput
                                                        disabled={field.disabled}
                                                        value={field.value}
                                                        onBlur={field.onBlur}
                                                        onChange={(value, data, event, formattedValue) => {
                                                            field.onChange(formattedValue);
                                                        }}
                                                        //
                                                        buttonStyle={{
                                                            border: 'none',
                                                        }}
                                                        specialLabel=''
                                                        country={country!.code!.toLowerCase()}
                                                        inputStyle={{
                                                            border: 'none',
                                                            height: '100%',
                                                            borderRadius: '0',
                                                            width: '100%',
                                                        }}
                                                        containerStyle={{
                                                            border: '1px solid #0000003b',
                                                            borderRadius: '0',
                                                        }}
                                                        enableAreaCodeStretch
                                                        enableAreaCodes
                                                        areaCodes={AREA_CODES}
                                                        masks={{ mx: '... .... ....' }}
                                                    />
                                                    <FormHelperText
                                                        sx={{
                                                            fontSize: 9,
                                                            color: fieldState.error
                                                                ? theme.palette.error.main
                                                                : theme.palette.primary.main,
                                                        }}
                                                    >
                                                        {fieldState.error
                                                            ? _('El formato del número de teléfono es incorrecto.')
                                                            : ''}
                                                    </FormHelperText>
                                                </FormControl>
                                            );
                                        }}
                                    />
                                </Grid2>
                            </FormSection>

                            <FormSection title={_('Tipo de usuario')}>
                                <Grid2 xs={12} md={6}>
                                    <MuiSelect
                                        formControlProps={{ fullWidth: true }}
                                        inputLabel={_('Rol')}
                                        options={settings.choices.userTypes || []}
                                        error={!!form.formState.errors.type}
                                        disabled={user?.validated_type}
                                        value={form.watch('type')}
                                        {...form.register('type')}
                                    />
                                </Grid2>
                                {form.watch('type') === 'bayer' && (
                                    <Grid2 xs={12} md={6}>
                                        <MuiSelect
                                            formControlProps={{ fullWidth: true }}
                                            inputLabel={_('Segmento')}
                                            options={settings.choices.userSubtypes || []}
                                            error={!!form.formState.errors.subtype}
                                            disabled={user?.validated_type}
                                            value={form.watch('subtype')}
                                            {...form.register('subtype')}
                                        />
                                    </Grid2>
                                )}
                            </FormSection>
                        </Stack>

                        <FormSection title={_('Notificaciones')}>
                            <Stack direction='column' spacing={1}>
                                <FormControlLabel
                                    {...form.register('receiveNotifications')}
                                    control={<Checkbox checked={form.watch('receiveNotifications')} />}
                                    label={_('Acepto recibir emails y mensajes de {{appName}}', {
                                        appName: settings.app.name,
                                    })}
                                />

                                <FormControlLabel
                                    {...form.register('receiveWhatsappNotifications')}
                                    control={<Checkbox checked={form.watch('receiveWhatsappNotifications')} />}
                                    label={_('Acepto recibir mensajes de WhatsApp de {{appName}}', {
                                        appName: settings.app.name,
                                    })}
                                />
                            </Stack>
                        </FormSection>

                        <Button
                            variant='text'
                            color='primary'
                            onClick={() => setOpenDeleteAccountPopup(true)}
                            sx={{ width: 'fit-content' }}
                        >
                            {_('Eliminar cuenta')}
                        </Button>
                    </Stack>
                </Grid2>
            </Grid2>

            <DeleteAccountModal open={openDeleteAccountPopup} setOpen={setOpenDeleteAccountPopup} />
        </TabContainer>
    );
});

export default ProfileEditTab;
